import _ from "lodash";
import React, { useCallback, useRef, useState, useMemo } from "react";
import { compose } from "lib/apolloClient";
import { connect } from "react-redux";
import { colors, SIMILARITY_FILE_MIMETYPE } from "Constants";
import classes from "./StudentResponse.scss";
import { AttachmentList, CreateDropDown } from "Attachment";
import I18nHOC from "UIComponents/I18nHOC";
import TextAreaInput from "UIComponents/TextAreaInput";
import StudentTemplates from "UIComponents/StudentTemplates";
import UIBaseComponent from "UIComponents/UIBaseComponent";
import {
  editStudentAssignmentSubmissionWithDebounce,
  editStudentAssignmentSubmission,
  getAssignmentDeletedStatus,
  getToddleDocValue,
} from "ClassRoom/modules/ClassRoomModule";
import { replaceEditPreview, openLinkInNewTab } from "modules/Services";
import { setToastMsg } from "Login/modules/LoginModule/LoginActions";
import {
  getAttachmentsToDisplayWithPostConfig,
  STUDENT_ASSIGNMENT_MEDIA_CREATION_OPTIONS,
  getClassroomPostDetails,
  getShowAssessmentTool,
  getSubmissionStatus,
  getIsIncreaseStudentSubmissionAudioLimit,
  MAX_AUDIO_RECORD_LIMIT,
  getIsLTIAttachmentPresent,
} from "ClassRoom/modules/utils";
import { getIsCurrentAcademicYear } from "Platform/modules/PlatformModule";
import SimilarityIndexComponent from "AppComponents/SimilarityIndexComponent";
import { getSettingValue } from "modules/PermissionModule";
import {
  AddOutlined,
  JournalAddOutlined,
  JournalTickOutlined,
  PostTwoOutlined,
} from "@toddle-edu/ds-icons";
import { Button, IconButton, EmptyState } from "@toddle-edu/ds-web";
import { CreatePost } from "Post";
import {
  isDPSubjectJournalEnabled,
  isPortfolioEnabled,
} from "AppComponents/Post/utils";
import { EvidenceIllustration } from "@toddle-edu/ds-theme";
import { getSelectedCurriculumId } from "modules/CommonSelectors";

const mediaCreationOptions = STUDENT_ASSIGNMENT_MEDIA_CREATION_OPTIONS;

const INITIAL_POST_DATA_STATE = {
  mediaData: {},
  showMediaCreation: false,
  createPostTitle: "",
};

const styles = {
  attachmentContainerStyle: {
    rowGap: "16px",
    display: "flex",
    flexDirection: "column",
  },
  createDropDownContainerStyle: {
    height: "80px",
    border: `1px dashed ${colors.strokeTwo}`,
    borderRadius: "8px",
  },
  wrapperStyle: {
    height: "64px",
    justifyContent: "center",
    backgroundColor: colors.surfaceSubtle,
    padding: "8px 16px 8px 8px",
    border: "none",
    borderRadius: "8px",
  },
  templateContainerStyle: {
    display: "flex",
    flexDirection: "column",
    rowGap: "16px",
  },
};

const StudentResponse = React.memo(props => {
  const {
    editStudentAssignmentSubmissionWithDebounce,
    editStudentAssignmentSubmission,
    setToastMsg,
    submission,
    updateSubmission,
    isRemovedCourseForLoggedInUser,
    studentAssignmentId,
    assessmentTools,
    setUploadingStatus,
    t,
    content,
    onMediaClick,
    contentType,
    assignedStudents,
    userType,
    attachmentResponseObject,
    refetchGetStudentAssignmentDetails,
    onUseTemplateClick,
    getIsCurrentAcademicYear,
    isSimilarityCheckEnabled,
    isArchivedClass,
    assignmentId,
    assessmentId,
    shouldShowSubmissionsToFamily,
    course,
    isTeacherEvaluationEnabled,
    isSharedWithStudent,
    currentCurriculumProgramType,
    isScheduled,
    resetStudentTemplate,
    isTimedTestEnabled,
    isResponseSectionEmpty,
    assessmentFields,
    goBack,
    overrideSubjectTypeCheck = false,
    getToddleDocValue,
  } = props;

  const [postData, setPostData] = useState({
    mediaData: {},
    showMediaCreation: false,
    createPostTitle: "",
  });

  const createDropDownRef = useRef();
  const checkIsDeleted = useRef(true);

  const isCurrentAcademicYear = getIsCurrentAcademicYear();
  const submissionId = _.get(submission, "id", "");
  const response = _.get(submission, "response", "") || "";
  const attachments = _.get(submission, "attachments", []) || [];
  const attachmentGroups = _.get(submission, "attachmentGroups", []) || [];
  const { isSubmitted } = getSubmissionStatus({
    submission,
  });

  const [localResponse, setLocalResponse] = useState(response);

  const isParent = userType === "parent";
  const studentAssignment =
    _.find(assignedStudents, { id: studentAssignmentId }) || {};
  const isEvaluated = _.get(studentAssignment, "isEvaluated", false);
  const isEvaluatedOrSubmitted = isEvaluated || isSubmitted;
  const isSubmissionStateLocked =
    _.get(studentAssignment, "lockingState.state", "") === "LOCKED";
  const isSubmissionLockedForStudent =
    (!isTimedTestEnabled ? isSubmissionStateLocked : isEvaluated) ||
    isSubmitted;
  const mode =
    isSubmissionLockedForStudent ||
    isRemovedCourseForLoggedInUser ||
    isArchivedClass ||
    isParent ||
    !isCurrentAcademicYear
      ? "view"
      : "edit";
  const studentTemplate = _.find(content.allFields, {
    uid: "studentTemplate",
  });

  const templateGroups =
    contentType == "ASSESSMENT"
      ? _.get(studentTemplate, "resolvedMinimalTree.attachmentGroups", [])
      : _.get(content, "studentTemplates", []);

  const ltiTemplates =
    _.get(
      _.find(assessmentFields, {
        uid: "ltiTemplate",
      }),
      "resolvedMinimalTree.attachmentGroups",
      []
    ) || [];

  const attachmentsData = getAttachmentsToDisplayWithPostConfig({
    attachmentGroups,
    attachments,
  });
  const attachmentsToDisplay = _.cloneDeep(attachmentsData);

  // Disabled mode is for parent only
  const isDisabledModeObj = {};
  if (userType === "parent") {
    _.map(attachmentsToDisplay, attachment => {
      // key: id of attachment, value: boolean
      const key = _.get(attachment, "id", "");
      const value =
        _.get(attachment, "attachments.0.type", "") === "LINK" &&
        _.get(attachment, "attachments.0.mimeType", "") !== null &&
        !shouldShowSubmissionsToFamily;
      const url = _.get(attachment, ["attachments", "0", "url"], "");

      attachment["attachments"][0].url = replaceEditPreview(url);

      isDisabledModeObj[key] = value;
    });
  }

  const workspaceAttachments = _.filter(attachmentsToDisplay, attachment => {
    return _.isEmpty(_.get(attachment, "studentTemplate", {}));
  });

  const someToolHaveData = _.some(assessmentTools, ({ node }) => {
    const assessmentTool = _.get(node, "assessmentToolItem", {});
    const assessmentToolType = _.get(node, "assessmentToolType", "");
    return getShowAssessmentTool({
      assessmentTool,
      assessmentToolType,
      allFields: _.get(content, "allFields", []),
    });
  });

  const isStudent = _.isEqual(userType, "student");

  const debouncedUpdateTextArea = useMemo(
    () =>
      _.debounce(param => {
        updateSubmission({ response: param });
        editStudentAssignmentSubmissionWithDebounce({
          id: submissionId,
          response: param,
        });
      }, 500),
    [submissionId]
  );

  const updateTextArea = useCallback(
    async param => {
      // 1. Check if the assignment is deleted only once.
      if (checkIsDeleted.current) {
        // 2. Optimistically update the local state to avoid user-facing lag
        // while awaiting the response from getAssignmentDeletedStatus.
        setLocalResponse(param);
        const isDeleted = await getAssignmentDeletedStatus(assignmentId);

        // 3. if assignment is deleted then send user back with toast msg
        if (isDeleted) {
          goBack();
          setToastMsg("toastMsgs:trying_to_access_deleted_post");
        } else {
          // 4. if not deleted then call the mutation and set checkIsDeleted = false
          debouncedUpdateTextArea(param);
          checkIsDeleted.current = false;
        }
      } else {
        debouncedUpdateTextArea(param);
        setLocalResponse(param);
      }
    },
    [debouncedUpdateTextArea]
  );

  const updateTextAreaOnBlur = useCallback(
    async ({ params }) => {
      // cancel debounce mutation call
      debouncedUpdateTextArea?.cancel();

      updateSubmission({ response: params });
      const res = await editStudentAssignmentSubmission({
        mutationParams: {
          id: submissionId,
          response: params,
        },
      });
      // if unsuccessful show toast an throw error to avoid message loss
      if (!res.successful) {
        setToastMsg(res.errors[0]);
      }
    },
    [submissionId]
  );

  const updateAttachments = useCallback(updatedAttachments => {
    const attachmentsToUpdate = _.filter(
      updatedAttachments,
      updatedAttachment =>
        _.isEmpty(_.get(updatedAttachment, "attachments", []))
    );

    updateSubmission({
      attachments: attachmentsToUpdate,
    });
  }, []);

  mediaCreationOptions["instructionsData"] = { content, contentType };

  const attachmentBottomComponent = params => {
    const { attachment } = params;
    const isPost = _.get(attachment, "isPost", false);
    const attachmentObj = isPost
      ? _.get(attachment, ["attachments", "0"], {})
      : attachment;
    const { type, mimeType } = attachmentObj;
    const getIsTurnitinAllowedMimeType = () => {
      return (
        (type == "FILE" || type == "LINK") &&
        _.includes(SIMILARITY_FILE_MIMETYPE, mimeType)
      );
    };

    return isSimilarityCheckEnabled && getIsTurnitinAllowedMimeType() ? (
      <SimilarityIndexComponent
        attachment={attachmentObj}
        metadata={{
          entityId: submissionId,
          entityType: "STUDENT_ASSIGNMENT_SUBMISSION",
          additionalData: {
            assignmentId,
          },
        }}
        course={course}
        isParent={isParent}
      />
    ) : null;
  };

  const onAddWorkButtonClicked = () => {
    createDropDownRef?.current?.toggleMediaCreation?.(true);
  };

  const addWorkButtonComponent = (
    <div className={classes.addWorkButton}>
      <Button
        size={"medium"}
        variant={"outlined"}
        icon={<AddOutlined />}
        onClick={onAddWorkButtonClicked}
        isFullWidth={false}
        testId="studentAssignment-addWork"
      >
        {t("common:add_work")}
      </Button>
    </div>
  );

  const createDropDownRefFunction = ref => {
    createDropDownRef.current = ref;
  };

  const onClickCreatePost = async attachment => {
    let attachmentData = _.omit(attachment, [
      "isPost",
      "createdBy",
      "similarityReport",
      "isRead",
      "questionAttachment",
      "signedUrl",
    ]);

    if (
      _.get(attachment, "type") === "TODDLE_DOC" &&
      !_.isEmpty(attachment?.attachmentId)
    ) {
      try {
        const result = await getToddleDocValue({
          id: attachment?.attachmentId,
        });
        const toddleDoc = _.get(result, "toddleDoc");
        if (toddleDoc) {
          attachmentData = {
            ...attachmentData,
            toddleDoc: _.omit(toddleDoc, ["__typename"]),
          };
        }
      } catch (e) {
        console.error("Failed to fetch toddleDoc value for createPost", e);
      }
    }

    const postData = getClassroomPostDetails({
      attachments: [attachmentData],
      student: _.get(_.head(assignedStudents), "student"),
      content,
      title: response,
      curriculumProgramType: currentCurriculumProgramType,
      courseId: _.get(course, "id", ""),
    });

    setPostData({
      mediaData: {
        ...postData,
        customPlaceHolder: "journal:share_learned",
        shouldKeepWorkbookId: false,
        isAddStudentAttachmentToJournal: true,
        activityLog: {
          entityId: studentAssignmentId,
          entityType: "STUDENT_ASSIGNMENT",
        },
      },
      showMediaCreation: true,
      createPostTitle: t("classRoom:add_response_to_portfolio"),
    });
  };

  const onCloseCreatePost = () => {
    setPostData({
      mediaData: {},
      showMediaCreation: false,
      createPostTitle: "",
    });
  };

  const isResponseEmpty =
    _.isEmpty(templateGroups) &&
    _.isEmpty(workspaceAttachments) &&
    _.isEmpty(response) &&
    _.isEmpty(ltiTemplates);

  const isCoreSubjectClass = _.get(course, "subjectGroups.0.type") === "CORE";
  const isLTIToolPresent =
    !_.isEmpty(ltiTemplates) ||
    _.chain(templateGroups)
      .flatMap("attachments")
      .some(attachment => _.get(attachment, "metadata.type") === "LTI_TOOL")
      .value();

  const showAddToJournal =
    isPortfolioEnabled() &&
    isCurrentAcademicYear &&
    isStudent &&
    isEvaluatedOrSubmitted &&
    (!isCoreSubjectClass || overrideSubjectTypeCheck) &&
    !isLTIToolPresent;

  const getViewModeExtraButtons = ({ attachment }) => {
    const isLTITool =
      _.isEqual(_.get(attachment, "type"), "LINK") &&
      _.isEqual(_.get(attachment, "metadata.type", ""), "LTI_TOOL");

    const onClickCreatePostLocal = e => {
      e.stopPropagation();
      if (onClickCreatePost) {
        onClickCreatePost(attachment);
      }
    };

    return (
      showAddToJournal &&
      !isLTITool && (
        <IconButton
          onClick={onClickCreatePostLocal}
          variant={"plain"}
          icon={<JournalAddOutlined />}
          testId="studentAssignment-addToJournal"
        />
      )
    );
  };

  const studentTemplatesArr = [...templateGroups, ...ltiTemplates];

  const isWorkspaceAttachmentsEmpty = _.isEmpty(workspaceAttachments);
  return !isResponseSectionEmpty ? (
    <div className={classes.container}>
      <div className={classes.body}>
        <div className={classes.innerBody}>
          {!isRemovedCourseForLoggedInUser && !isScheduled ? (
            mode === "view" ? (
              <div className={classes.responseText}>{localResponse}</div>
            ) : (
              <TextAreaInput
                minRows={5}
                placeholder={t("common:type_remark_here")}
                value={localResponse}
                mode={mode}
                updateInputField={updateTextArea} // debounce mutation call
                onBlurInputField={updateTextAreaOnBlur} // mutation call
                showViewEmpty={false}
                testId={"studentAssignment-addResponse"}
              />
            )
          ) : null}

          {!_.isEmpty(studentTemplatesArr) ? (
            <div className={classes.studentTemplatesContainer}>
              <StudentTemplates
                value={studentTemplatesArr}
                canTurnIn={
                  !isArchivedClass &&
                  !isScheduled &&
                  isCurrentAcademicYear &&
                  !isSubmissionLockedForStudent
                }
                submissionId={submissionId}
                refetchGetStudentAssignmentDetails={
                  refetchGetStudentAssignmentDetails
                }
                myWorkAttachments={attachmentsToDisplay}
                onUseTemplateClick={onUseTemplateClick}
                templateAttachmentWrapperStyle={styles.wrapperStyle}
                templateContainerStyle={styles.templateContainerStyle}
                attachmentResponseObject={attachmentResponseObject}
                onMediaClick={onMediaClick}
                showMediaCard={true}
                attachmentBottomComponent={attachmentBottomComponent}
                onClickCreatePost={onClickCreatePost}
                showAddToJournal={showAddToJournal}
                isSubmitted={isSubmitted}
                resetStudentTemplate={resetStudentTemplate}
                isScheduled={isScheduled}
                isArchivedClass={isArchivedClass}
                isEvaluated={isEvaluated}
                isTeacherEvaluationEnabled={isTeacherEvaluationEnabled}
                isSharedWithStudent={isSharedWithStudent}
                studentAssignmentId={studentAssignmentId}
                isSubmissionLockedForStudent={isSubmissionLockedForStudent}
                isSubmissionStateLocked={isSubmissionStateLocked}
              />
            </div>
          ) : null}
          {!isWorkspaceAttachmentsEmpty && (
            <AttachmentList
              cardType={"design-media-card"}
              isDisabledModeObj={isDisabledModeObj}
              mode={mode}
              value={workspaceAttachments}
              shouldCreateAttachmentGroup={true}
              shouldCallMutation={true}
              showCreateDropdown={false}
              parentId={submissionId}
              shouldCreateAsMedia={true}
              toolTipText={t(
                "classRoom:document_accessible_on_student_account"
              )}
              parentType={`STUDENT_ASSIGNMENT_SUBMISSION`}
              attachmentResponseObject={attachmentResponseObject}
              attachmentContainerStyle={styles.attachmentContainerStyle}
              createDropDownContainerStyle={styles.createDropDownContainerStyle}
              updateInputField={updateAttachments}
              mediaCreationOptions={mediaCreationOptions}
              updateLoadingState={setUploadingStatus}
              onClickAttachmentCard={(id, attachmentGroup) =>
                onMediaClick(attachmentGroup, false, true)
              }
              attachmentBottomComponent={attachmentBottomComponent}
              showViewEmpty={false}
              showCreatePostButton={showAddToJournal}
              onClickCreatePost={onClickCreatePost}
              getViewModeExtraButtons={getViewModeExtraButtons}
              showIconButton={true}
            />
          )}
          {mode === "edit" &&
          !isParent &&
          !isScheduled &&
          isCurrentAcademicYear ? (
            <CreateDropDown
              createDropDownRef={createDropDownRefFunction}
              attachments={workspaceAttachments}
              mediaCreationOptions={mediaCreationOptions}
              shouldCreateAsMedia={true}
              updateLoadingState={setUploadingStatus}
              setToastMsg={setToastMsg}
              parentId={submissionId}
              parentType={`STUDENT_ASSIGNMENT_SUBMISSION`}
              shouldCallMutation={true}
              updateAttachments={updateAttachments}
              buttonComponent={addWorkButtonComponent}
              audioRecordingLimit={
                getIsIncreaseStudentSubmissionAudioLimit()
                  ? MAX_AUDIO_RECORD_LIMIT
                  : undefined
              }
              assessmentId={assessmentId}
              assignmentId={assignmentId}
              allowMultipleFiles={true}
            />
          ) : null}

          {isParent && isResponseEmpty ? (
            <div className={classes.noDataContainer}>
              <EmptyState
                illustration={EvidenceIllustration}
                subtitle={t("classRoom:no_work_description")}
              />
            </div>
          ) : null}
        </div>
      </div>
      {_.get(postData, "showMediaCreation") && (
        <CreatePost
          onClose={onCloseCreatePost}
          postCreateDetail={_.get(postData, "mediaData")}
          currentStep={
            isDPSubjectJournalEnabled(currentCurriculumProgramType)
              ? "MANAGE_SUBJECT_JOURNAL_POST_DETAILS"
              : "POST_DETAIL"
          }
          createPostTitle={_.get(postData, "createPostTitle")}
        />
      )}
    </div>
  ) : null;
});

class StudentResponseWrapper extends UIBaseComponent {
  constructor(props) {
    super(props);

    this.state = INITIAL_POST_DATA_STATE;
  }

  shouldShowEmpty = () => {
    const {
      isStudentSubmissionEnabled,
      submission,
      getIsCurrentAcademicYear,
      assignedStudents,
      studentAssignmentId,
      isScheduled,
      isTimedTestEnabled,
    } = this.props;

    const { response } = submission || {};
    const {
      workspaceAttachments,
      templateGroups,
      ltiTemplates,
    } = this.getAttachments();
    const isResponseEmpty =
      _.isEmpty(templateGroups) &&
      _.isEmpty(ltiTemplates) &&
      _.isEmpty(workspaceAttachments) &&
      _.isEmpty(response);

    const { isSubmitted } = getSubmissionStatus({ submission });
    const studentAssignment =
      _.find(assignedStudents, { id: studentAssignmentId }) || {};

    const isEvaluated = _.get(studentAssignment, "isEvaluated", false);
    const isSubmissionStateLocked =
      _.get(studentAssignment, "lockingState.state", "") === "LOCKED";

    const isSubmissionLockedForStudent =
      (!isTimedTestEnabled ? isSubmissionStateLocked : isEvaluated) ||
      isSubmitted;

    const isResponseSectionEmpty =
      (isScheduled ||
        isSubmissionLockedForStudent ||
        !getIsCurrentAcademicYear()) &&
      isResponseEmpty;

    const showResponseSection =
      !isResponseSectionEmpty && isStudentSubmissionEnabled;

    return !showResponseSection;
  };

  navigateToPostDetail = () => {
    const { post, openLinkInNewTab } = this.props;
    const postId = _.get(post, "id", "");

    if (postId) {
      openLinkInNewTab({
        url: `/platform/links/post/${postId}/?preserve_query_params=true`,
      });
    }
  };

  getAttachments = () => {
    const { content, submission, contentType, assessmentFields } = this.props;

    const { attachmentGroups, attachments } = submission || {};

    const studentTemplate = _.find(content?.allFields, {
      uid: "studentTemplate",
    });

    const templateGroups =
      contentType == "ASSESSMENT"
        ? _.get(studentTemplate, "resolvedMinimalTree.attachmentGroups")
        : _.get(content, "studentTemplates", []);

    const attachmentsData = getAttachmentsToDisplayWithPostConfig({
      attachmentGroups,
      attachments,
    });

    const attachmentsToDisplay = _.cloneDeep(attachmentsData);

    const submittedTemplates = _.map(templateGroups, template => {
      const templateId = _.get(template, "id");
      return _.find(attachmentsToDisplay, item => {
        return _.get(item, "studentTemplate.id", "") === templateId;
      });
    });

    const workspaceAttachments = _.filter(attachmentsToDisplay, attachment => {
      return _.isEmpty(_.get(attachment, "studentTemplate", {}));
    });

    const ltiTemplates =
      _.get(
        _.find(assessmentFields, {
          uid: "ltiTemplate",
        }),
        "resolvedMinimalTree.attachmentGroups",
        []
      ) || [];

    return {
      templateGroups,
      workspaceAttachments,
      submittedTemplates,
      ltiTemplates,
    };
  };

  onAddToJournal = () => {
    const {
      t,
      assignedStudents,
      content,
      submission,
      currentCurriculumProgramType,
      course,
      studentAssignmentId,
      template,
      isStudentEvaluationEnabled,
      isTeacherEvaluationEnabled,
      isSharedWithStudent,
      elementRemarks,
    } = this.props;

    const studentAssignment =
      _.find(assignedStudents, { id: studentAssignmentId }) || {};
    const { isEvaluated } = studentAssignment || {};

    const { response } = submission || {};

    const { workspaceAttachments, submittedTemplates } = this.getAttachments();

    const classroomPostDetails = getClassroomPostDetails({
      student: _.get(_.head(assignedStudents), "student"),
      content,
      title: response,
      curriculumProgramType: currentCurriculumProgramType,
      courseId: _.get(course, "id", ""),
    });

    //filter submitted template based on lti tool
    const filteredSubmittedTemplates = _.filter(
      submittedTemplates,
      attachmentGroup => {
        const attachment = _.get(attachmentGroup, "attachments[0]");
        const isLTITool = _.isEqual(
          _.get(attachment, "metadata.type"),
          "LTI_TOOL"
        );
        return !isLTITool;
      }
    );
    const addLEToJournalData = _.omit(
      {
        ...classroomPostDetails,
        isAddLEToJournal: true,
        LETitle: _.get(content, "title.value", ""),
        studentAssignmentId: studentAssignmentId,
        items: [
          {
            item: {
              id: studentAssignmentId,
              assignment: {
                content: { ...content, ...template },
                id: studentAssignmentId,
                isStudentEvaluationEnabled: isStudentEvaluationEnabled,
                isTeacherEvaluationEnabled: isTeacherEvaluationEnabled,
              },
              ibPYPElementRatings: [],
              student: _.get(_.head(assignedStudents), "student"),
              submission: {
                attachmentGroups: _.concat(
                  workspaceAttachments,
                  filteredSubmittedTemplates
                ),
                response: response,
              },
              toolResponses: {},
              isSharedWithStudent,
              isEvaluated,
              elementRemarks: isSharedWithStudent ? elementRemarks : [],
            },
            itemType: "STUDENT_ASSIGNMENT",
          },
        ],
      },
      ["title"]
    );

    this.setState({
      postData: {
        mediaData: {
          ...addLEToJournalData,
          customPlaceHolder: "journal:share_learned",
          isSharedWithStudent,
        },
        showMediaCreation: true,
        createPostTitle: t("classRoom:add_response_to_portfolio"),
      },
    });
  };

  getAddToJournalState = post => {
    const state = _.get(post, "state");
    let Icon = JournalAddOutlined;
    let variant = "default";
    let text = "journal:add_to_portfolio";
    let textColor = colors.textDefault;
    let clickAction = this.onAddToJournal;

    switch (state) {
      case "IN_APPROVAL": {
        Icon = PostTwoOutlined;
        variant = "warning";
        text = "journal:sent_for_approval";
        textColor = colors.textWarning;
        clickAction = this.navigateToPostDetail;
        break;
      }
      case "PUBLISHED": {
        Icon = JournalTickOutlined;
        variant = "success";
        text = "classRoom:response_added_to_portfolio";
        textColor = colors.textSuccess;
        clickAction = this.navigateToPostDetail;
        break;
      }
    }

    return {
      Icon,
      variant,
      text,
      textColor,
      clickAction,
    };
  };

  renderLabel = () => {
    const {
      t,
      post,
      isScheduled,
      submission,
      getIsCurrentAcademicYear,
      userType,
      assignedStudents,
      studentAssignmentId,
      course,
      headerRef,
      childFirstName,
      assessmentFields,
    } = this.props;

    const { response, attachments, attachmentGroups } = submission || {};

    const attachmentsData = getAttachmentsToDisplayWithPostConfig({
      attachmentGroups,
      attachments,
    });

    const attachmentsToDisplay = _.cloneDeep(attachmentsData);

    const isLTIToolPresent = getIsLTIAttachmentPresent(assessmentFields);

    //this is for question submission , if submission is only of question then do not show addToJournal option
    const canAddToJournal =
      (!_.isEmpty(response) || !_.isEmpty(attachmentsToDisplay)) &&
      !isLTIToolPresent;

    const {
      Icon,
      variant,
      text,
      textColor,
      clickAction,
    } = this.getAddToJournalState(post);

    const studentAssignment =
      _.find(assignedStudents, { id: studentAssignmentId }) || {};

    const { isEvaluated, isSubmitted } = studentAssignment || {};

    const isEvaluatedOrSubmitted = isEvaluated || isSubmitted;
    const isCoreSubjectClass = _.get(course, "subjectGroups.0.type") === "CORE";

    const showAddToJournal =
      isPortfolioEnabled() &&
      getIsCurrentAcademicYear() &&
      userType === "student" &&
      isEvaluatedOrSubmitted &&
      (!isCoreSubjectClass || this.props.overrideSubjectTypeCheck);

    const isParent = userType === "parent";

    return (
      <div className={classes.responseHeader} ref={headerRef}>
        {!isScheduled && (
          <div className={classes.header}>
            {isParent
              ? t("classRoom:labels_work", { label: childFirstName })
              : t("common:response")}
          </div>
        )}
        {showAddToJournal && canAddToJournal && (
          <div
            className={classes.addToJournalContainer}
            onClick={clickAction}
            data-testid="studentAssignment-addToPortfolio"
          >
            <div className={classes.postIconContainer}>
              <Icon size={"xx-small"} variant={variant} />
            </div>
            <div
              className={classes.addToJournalLabel}
              style={{ color: textColor }}
            >
              {t(text)}
            </div>
          </div>
        )}
      </div>
    );
  };

  renderView = () => {
    const { postData, currentCurriculumProgramType } = this.state;

    const isResponseSectionEmpty = this.shouldShowEmpty();

    return (
      <>
        <StudentResponse
          {...this.props}
          isResponseSectionEmpty={isResponseSectionEmpty}
        />
        {_.get(postData, "showMediaCreation") ? (
          <CreatePost
            onClose={() => this.setState({ postData: INITIAL_POST_DATA_STATE })}
            postCreateDetail={_.get(postData, "mediaData")}
            currentStep={
              isDPSubjectJournalEnabled(currentCurriculumProgramType)
                ? "MANAGE_SUBJECT_JOURNAL_POST_DETAILS"
                : "POST_DETAIL"
            }
            createPostTitle={_.get(postData, "createPostTitle")}
          />
        ) : null}
      </>
    );
  };
}

const mapActionCreators = {
  editStudentAssignmentSubmissionWithDebounce,
  editStudentAssignmentSubmission,
  setToastMsg,
  getIsCurrentAcademicYear,
  openLinkInNewTab,
  getToddleDocValue,
};

const mapStateToProps = (state, ownProps) => {
  const userType = _.get(state, "login.userInfo.user_type");
  const userEntityType = _.get(state, "login.userInfo.userEntityType");
  const organizationId = state.login.userInfo.org_id;
  const currentCurriculumProgramType = _.get(
    state,
    "platform.currentCurriculumProgram.type",
    "IB_PYP"
  );

  let shouldShowSubmissionsToFamily;
  if (userType === "parent") {
    shouldShowSubmissionsToFamily = getSettingValue({
      name: "UnlockFamilySubmissions",
      courseId: ownProps.assignmentCourseId,
      organizationId,
      userEntityType,
    });
  }

  const isFinalCriteriaSettingEnabled = getSettingValue({
    name:
      userType === "student"
        ? "ShowFinalGradeRatingToStudent"
        : "ShowFinalGradeRatingToParent",
    organizationId,
    userEntityType,
  });

  const coreSubjectType = _.get(ownProps?.course, "subjects.0.type");
  const allowedSubjectTypes =
    getSettingValue({
      name: "EnablePortfolioForDpCoreChild",
      currentCurriculumProgramId: getSelectedCurriculumId(state),
    }) || [];
  const overrideSubjectTypeCheck = _.includes(
    allowedSubjectTypes,
    coreSubjectType
  );

  return {
    userType,
    shouldShowSubmissionsToFamily,
    isFinalCriteriaSettingEnabled,
    childFirstName: _.get(state, "login.userInfo.childFirstName", ""),
    currentCurriculumProgramType,
    overrideSubjectTypeCheck,
  };
};

StudentResponse.displayName = "StudentResponse";
StudentResponseWrapper.displayName = "StudentResponseWrapper";

export default compose(
  I18nHOC({
    resource: [
      "common",
      "classRoom",
      "toastMsgs",
      "studentGoals",
      "assessmentEvaluation",
      "journal",
    ],
  }),
  connect(mapStateToProps, mapActionCreators)
)(StudentResponseWrapper);
